// Strings for the platform admin workflows (0009_platform_admin.sql), the
// notification centre, and the head of school's Approvals card. Merged into
// STRINGS in strings.ts, so every language must define every key (checked
// by the type below). Finnish and Swedish need native-speaker review before
// go-live, like the rest of the app.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

const n = (count: number, one: string, many: string) => (count === 1 ? one : many);

export const platformEn = {
  // ── errors raised as swipe:<code> ──
  'err.generic': (detail: string) => `Something went wrong: ${detail}`,
  'err.not_authorized': 'You don’t have permission to do this in your current role.',
  'err.unknown': 'Something went wrong. Try again; if it keeps happening, check the platform log.',
  'err.ops_unreachable': 'The account service couldn’t be reached. Check your connection and try again.',
  'err.bad_request': 'Some details are missing. Fill in every field and try again.',
  'err.ack_missing': 'Record the head of school’s acknowledgement first.',
  'err.ack_required': 'Record who acknowledged it and on which date (not in the future).',
  'err.admins_without_mfa': 'Every platform admin must set up an authenticator before two-factor can be enforced.',
  'err.already_invited': 'This email already has an invite waiting.',
  'err.already_on_staff': 'This email is already on staff.',
  'err.already_requested': 'An erasure request for this person is already waiting for approval.',
  'err.bad_assignment': 'One of the classes or grades isn’t in this school. Reload and try again.',
  'err.bad_email': 'Enter a valid email address.',
  'err.bad_expiry': 'Choose an expiry between 1 and 90 days.',
  'err.bad_language': 'Choose a language.',
  'err.bad_paperwork': 'Choose a paperwork status.',
  'err.bad_role': 'Choose a role from the list.',
  'err.bad_section': 'Section names are 1–8 characters.',
  'err.bad_size': 'Expected size is a number from 1 to 200.',
  'err.bad_stage': 'That grade level isn’t in the school’s list. Add it under Classes → Naming convention.',
  'err.bad_step': 'Unknown checklist step.',
  'err.bad_test_login': 'The login couldn’t be linked to a test account. Try again.',
  'err.bad_type': 'Choose Sandbox or Customer.',
  'err.cannot_suspend': 'Only a school that is setting up or live can be suspended.',
  'err.cells_required': 'Tick at least one class in the grid.',
  'err.class_exists': 'That class already exists.',
  'err.class_has_sessions': 'This class has check-in history, so it can only be archived.',
  'err.confirm_mismatch': 'What you typed doesn’t match. Type it exactly to confirm.',
  'err.confirmation_required': 'Record whom at the school you confirmed this with.',
  'err.date_in_future': 'The date can’t be in the future.',
  'err.date_in_past': 'The reactivation date must be in the future.',
  'err.deactivate_first': 'Deactivate this person before erasing their data.',
  'err.duplicate_role': 'A role is listed twice.',
  'err.duplicate_section': 'A section is listed twice.',
  'err.hold_not_over': 'The 30-day hold isn’t over yet.',
  'err.instruction_required': 'Record the school’s written instruction (who sent it, and when) in the note.',
  'err.invites_customer_only': 'Sandbox schools use test accounts, not invites.',
  'err.last_school_admin': 'This is the school’s last school admin. Add another school admin first.',
  'err.mfa_session_needed': 'Sign in with your authenticator code first, then try again.',
  'err.name_required': 'Enter a name.',
  'err.no_active_year': 'The school has no active academic year.',
  'err.no_session': 'Your sign-in has no session id. Sign out and in again.',
  'err.not_a_head': 'Only an active deputy head of school can become the primary.',
  'err.not_a_teacher': 'Give this person the Teacher role first.',
  'err.not_admin_role': 'Two-factor is reset only for people with an admin role.',
  'err.not_in_school': 'This person isn’t on this school’s staff.',
  'err.not_platform_admin': 'That person isn’t a platform admin.',
  'err.not_sandbox': 'Only a Sandbox school can become a Customer school.',
  'err.not_suspended': 'This school isn’t suspended.',
  'err.not_test_account': 'Only test-account passwords can be reset. Real staff have no password in Swipe.',
  'err.not_yourself': 'Another platform admin must reset your own two-factor.',
  'err.note_required': 'Say why in a short note.',
  'err.own_request': 'You can’t decide a request you asked for or one about yourself.',
  'err.paperwork_signed_needs': 'Signed paperwork needs the date and the signatory’s name.',
  'err.person_not_active': 'This person isn’t active. Reactivate them first.',
  'err.person_not_deactivated': 'This person isn’t deactivated.',
  'err.primary_head': 'This is the primary head of school. Move the primary mark to a deputy first.',
  'err.primary_head_decides': 'This school has a primary head of school, who decides this request.',
  'err.reason_required': 'Choose a reason (and fill in any required details).',
  'err.request_expired': 'This request has expired.',
  'err.request_gone': 'This request has already been decided or withdrawn.',
  'err.role_exists': 'This person already has this role, or a request for it.',
  'err.role_required': 'Choose at least one role.',
  'err.school_name_taken': 'A school with this name already exists.',
  'err.school_not_active': 'This school is suspended or has left Swipe.',
  'err.school_not_ready': 'Check-ins open once the school has finished setup.',
  'err.school_offboarded': 'This school has left Swipe.',
  'err.second_admin_needed': 'A second platform admin must decide this request.',
  'err.sections_required': 'Keep at least one section.',
  'err.stage_in_use': 'A grade level with classes can’t be removed. Archive its classes first.',
  'err.support_required': 'Enter support mode for this school first.',
  'err.test_accounts_remain': 'Erase the school’s test accounts first.',
  'err.test_accounts_sandbox_only': 'Test accounts exist only in Sandbox schools.',
  'err.test_expired': 'This test account has expired. Extend it first.',
  'err.tier_required': 'A Customer school needs a tier.',
  'err.too_long': 'Keep each message under 600 characters.',
  'err.two_admins_needed': 'Zencera needs at least two platform admins before two-factor is enforced.',
  'err.unknown_assignment': 'This assignment has already ended.',
  'err.unknown_class': 'Unknown class. Reload and try again.',
  'err.unknown_invite': 'This invite has already been used or cancelled.',
  'err.unknown_municipality': 'Choose a municipality.',
  'err.unknown_person': 'This person has already been erased.',
  'err.unknown_role': 'This role has already been removed.',
  'err.unknown_school': 'Unknown school.',

  // ── navigation, shared ──
  'pa.navApprovals': 'Approvals',
  'pa.navApprovalsN': (c: number) => `Approvals (${c})`,
  'pa.navLog': 'Platform log',
  'pa.navArchive': 'Archive',
  'pa.navSettings': 'Settings',
  'pa.working': 'Working…',
  'pa.save': 'Save',
  'pa.saved': 'Saved.',
  'pa.close': 'Close',
  'pa.keep': 'Keep it',
  'pa.back': '← Back',
  'pa.next': 'Next',
  'pa.add': 'Add',
  'pa.edit': 'Edit',
  'pa.remove': 'Remove',
  'pa.withdraw': 'Withdraw request',
  'pa.view': 'View',
  'pa.hide': 'Hide',
  'pa.download': 'Download',
  'pa.copy': 'Copy',
  'pa.copied': 'Copied',
  'pa.copyFailed': 'Couldn’t copy. Select the text and copy it yourself.',
  'pa.copyCode': 'Copy the school code',
  'pa.copyUsername': 'Copy the username',
  'pa.copyPassword': 'Copy the password',
  'pa.optional': 'Optional.',
  'pa.note': 'Note',
  'pa.noteOptional': 'Note (optional)',
  'pa.reason': 'Reason',
  'pa.status': 'Status',
  'pa.done': 'Done',
  'pa.notDone': 'Not done',
  'pa.yes': 'Yes',
  'pa.missing': 'Missing',
  'pa.never': 'never',
  'pa.nothing': 'nothing',
  'pa.you': 'you',
  'pa.on': 'On',
  'pa.off': 'Off',
  'pa.actions': 'Actions',
  'pa.loadMore': 'Load more',
  'pa.name': 'Name',
  'pa.email': 'Email',
  'pa.language': 'Language',
  'pa.schoolDefault': 'School’s default',
  'pa.lang_fi': 'Finnish',
  'pa.lang_sv': 'Swedish',
  'pa.lang_en': 'English',
  'pa.tier_a': 'Tier A',
  'pa.tier_b': 'Tier B',
  'pa.tier_aDesc': 'Shared database. The school’s data sits in Swipe’s shared database, kept apart from other schools by access rules. Standard price.',
  'pa.tier_bDesc': 'Dedicated database. The school gets its own separate database, isolated from every other school, still managed from this platform. Premium price.',
  'pa.tierHint': 'Choosing Tier B asks Zencera to set up the dedicated database. The school stays in Setting up until Zencera confirms it is ready.',
  'pa.comingLater': 'Coming later',
  'pa.tier_bSoon': 'Not available yet. It opens once Zencera can set up and run dedicated databases from this platform.',
  'err.tier_b_unavailable': 'Tier B (dedicated database) isn’t available yet. Choose Tier A.',
  'pa.tier_aShort': 'Tier A · shared database',
  'pa.tier_bShort': 'Tier B · dedicated database',
  'pa.school': 'School',
  'pa.person': 'Person',

  // ── overview ──
  'pa.statLive': 'Live schools',
  'pa.statLiveSub': (setting: number, suspended: number) => `${setting} setting up · ${suspended} suspended`,
  'pa.statApprovals': 'Approvals waiting',
  'pa.statApprovalsSub': 'for Zencera to decide',
  'pa.statTests': 'Test accounts',
  'pa.statTestsSub': (expiring: number) => `${expiring} expire within 7 days`,
  'pa.addSchoolDesc': 'Sandbox or Customer, with its first people.',
  'pa.approvalsDesc': 'Requests that only Zencera can decide.',
  'pa.logDesc': 'Every cross-school action, append-only.',
  'pa.attnSetup': (s: string, d: number, total: number) => `${s}: setup ${d} of ${total} required items done.`,
  'pa.attnSeeChecklist': 'See checklist',
  'pa.attnOffboardReady': (s: string) => `${s}: the 30-day hold is over; ready to offboard.`,
  'pa.attnOffboardAck': (s: string) => `${s}: hold over, but the head’s acknowledgement isn’t recorded.`,
  'pa.attnNoSchoolAdmin': (s: string) => `${s}: no active school admin.`,
  'pa.attnOneAdmin': 'Zencera has only one platform admin. Add a second one (README §6).',
  'pa.attnApprovals': (c: number) => `${c} ${n(c, 'request is', 'requests are')} waiting for Zencera.`,
  'pa.attnReview': 'Review',
  'pa.attnTestsExpired': (c: number) => `${c} expired test ${n(c, 'account is', 'accounts are')} waiting to be erased.`,

  // ── schools list ──
  'pa.searchSchools': 'Search by name or code',
  'pa.filterType': 'Filter by type',
  'pa.filterState': 'Filter by state',
  'pa.allTypes': 'All types',
  'pa.allStates': 'All states',
  'pa.stateNotOffboarded': 'All but offboarded',
  'pa.noMatch': 'No schools match these filters.',
  'pa.municipality': 'Municipality',
  'pa.staff': 'Staff',
  'pa.nTest': (c: number) => `${c} test`,
  'pa.lastActivity': 'Last activity',
  'pa.setupProgress': (d: number, total: number) => `Setup: ${d} of ${total} required`,
  'pa.agreementShort': 'Agreement',
  'pa.dpaShort': 'DPA',
  'pa.paperworkPill': (doc: string, status: string) => `${doc}: ${status}`,
  'pa.pwNotStarted': 'Not started',
  'pa.pwSent': 'Sent',
  'pa.pwSigned': 'Signed',

  // ── add school wizard ──
  'pa.wizSteps': 'Steps',
  'pa.wizStepOf': (i: number, total: number, name: string) => `Step ${i} of ${total}: ${name}`,
  'pa.wizType': 'Type',
  'pa.wizDetails': 'Details',
  'pa.wizPaperwork': 'Paperwork',
  'pa.wizPeople': 'First people',
  'pa.wizReview': 'Review',
  'pa.schoolType': 'School type',
  'pa.sandboxDesc': 'Pilot school. The school is the data controller and signs a sandbox agreement. Staff use test accounts made by Zencera; no email is sent.',
  'pa.customerDesc': 'Paying school under a full data processing agreement. Staff join by email invite and sign in with a one-time code.',
  'pa.typeHint': 'A Sandbox school can later become a Customer school, never the other way.',
  'pa.chooseMunicipality': 'Choose a municipality…',
  'pa.codeFromMunicipality': 'The school code is generated from it (FI-HEL-7KQ2) and never changes.',
  'pa.officialNumber': 'Official school number (oppilaitosnumero)',
  'pa.stages': 'Grade levels',
  'pa.stagesHint': 'These become the rows of the class grid. You can change them later.',
  'pa.levelsBasic': 'Basic education',
  'pa.levelsUpper': 'Upper secondary',
  'pa.levelsVocational': 'Vocational',
  'pa.defaultLanguage': 'Default language',
  'pa.tier': 'Tier',
  'pa.wizStagesNeeded': 'Choose at least one grade level.',
  'pa.agreement': 'Sandbox agreement',
  'pa.dpa': 'Data processing agreement',
  'pa.paperworkSandboxHint': 'Only Zencera sets this. The school sees it read-only on its checklist; check-ins open once it’s signed.',
  'pa.paperworkCustomerHint': 'Only Zencera sets this. Check-ins open once the DPA is signed and setup is complete.',
  'pa.signedOn': 'Signed on',
  'pa.signatory': 'Signed by (the school’s signatory)',
  'pa.signedBy': (who: string, date: string) => `Signed by ${who}, ${date}`,
  'pa.peopleSandboxNote': 'Test accounts are made now for these roles, with generated usernames and passwords shown once. Use fictional names — the school keeps its own list of who holds which account.',
  'pa.peopleCustomerNote': 'Invites are queued now and sent once an email provider is set up. Each person signs in with a one-time code; no password is created.',
  'pa.headName': 'Head of school (primary)',
  'pa.headNameTest': 'Head of school (primary) — fictional name',
  'pa.fictionalExample': 'e.g. Helmi Koivu (TEST)',
  'pa.workEmail': 'Work email',
  'pa.sameAdmin': 'The head of school is also the school admin',
  'pa.adminName': 'School admin',
  'pa.adminNameTest': 'School admin — fictional name',
  'pa.wizHeadNeeded': 'Enter the head of school’s name.',
  'pa.wizAdminNeeded': 'Enter the school admin’s name.',
  'pa.firstPeople': 'First people',
  'pa.reviewSame': (h: string) => `${h} — primary head of school and school admin`,
  'pa.reviewTwo': (h: string, a: string) => `${h} — primary head of school; ${a} — school admin`,
  'pa.afterCreate': 'When you create it',
  'pa.afterCreateSandbox': 'You enter support mode for the school (logged) and the test accounts are made.',
  'pa.afterCreateCustomer': 'You enter support mode for the school (logged) and the invites are queued.',
  'pa.createSchool': 'Create school',
  'pa.codeHint': 'Staff type this code on the sign-in page. Share it with the school.',
  'pa.credsTitle': 'Test account sign-in details',
  'pa.credsOnce': 'Shown only now. Give the password through a different channel from the username — in person, by phone or another message — never together. It only works once: at first sign-in the person must choose their own password before anything opens.',
  'pa.username': 'Username',
  'pa.password': 'Password',
  'pa.invitesQueuedNote': 'Invites are queued: no email provider is set up yet, so nothing is sent. They go out automatically once it is.',
  'pa.peopleFailed': 'The school was created, but these people weren’t added. Add them from the school’s Staff tab:',
  'pa.openSchool': 'Open the school',

  // ── school page ──
  'pa.schoolSummary': 'School summary',
  'pa.schoolSections': 'School sections',
  'pa.inSupportPill': 'Support mode on',
  'pa.inSupportHint': 'You’re in support mode for this school. Everything you do here is logged and visible to the school.',
  'pa.enterSupport': 'Enter support mode',
  'pa.enterSupportGo': 'Enter support mode',
  'pa.enterSupportIntro': 'Support mode lets you change this school’s staff, classes and settings for two hours. A banner stays on every screen, every action shows in the school’s own Activity, and results stay hidden.',
  'pa.supportEntered': (s: string) => `Support mode on for ${s}.`,
  'pa.supportLeft': 'Support mode ended.',
  'pa.supportRegion': 'Support mode',
  'pa.supportBanner': 'Support mode — Zencera. Actions are logged and visible to the school.',
  'pa.supportEnds': (m: number) => `ends in ${m} min`,
  'pa.supportLeave': 'Leave support mode',
  'pa.supportNeeded': (what: string) => `To ${what}, enter support mode for this school.`,
  'pa.sr_school_asked': 'The school asked for help',
  'pa.sr_setup_stuck': 'Setup is stuck',
  'pa.sr_account_recovery': 'Account recovery',
  'pa.sr_approval_no_primary_head': 'Approval with no primary head',
  'pa.sr_test_accounts': 'Test accounts',
  'pa.sr_other': 'Other',
  'pa.tabStaff': (c: number) => `Staff (${c})`,
  'pa.tabStaffPlain': 'Staff',
  'pa.tabClasses': (c: number) => `Classes (${c})`,
  'pa.tabSettings': 'Settings',
  'pa.tabActivity': 'Activity',
  'pa.checklist': 'Setup checklist',
  'pa.gateSandbox': 'Sandbox: only the safety-net message and the signed agreement block check-ins, because real students see the check-in screen. The rest are warnings.',
  'pa.gateCustomer': 'Customer: every required item must be done before check-ins open.',
  'pa.gateDone': 'Setup is complete enough for check-ins. Warnings still show to staff.',
  'pa.requiredTodo': 'Required',
  'pa.warningTodo': 'Warning',
  'pa.ckSafetyNet': 'Safety-net message (Finnish, Swedish, English)',
  'pa.ckPaperworkSandbox': 'Sandbox agreement signed',
  'pa.ckPaperworkCustomer': 'Data processing agreement signed',
  'pa.ckProfile': 'School profile, grade levels and sections confirmed',
  'pa.ckClasses': 'Classes created',
  'pa.ckStaffSandbox': 'Test accounts created',
  'pa.ckStaffCustomer': 'Staff invited',
  'pa.ckTeachers': 'Every teacher assigned to a class',
  'pa.ckNotice': 'Parent and student notice sent',
  'pa.ckDpo': 'Data protection officer sign-off',
  'pa.ckDedicated': 'Dedicated database confirmed by Zencera (Tier B)',
  'pa.confirmDedicated': 'Confirm setup',
  'pa.dedicatedTitle': 'Confirm the dedicated database',
  'pa.dedicatedIntro': 'Confirm only once the school’s dedicated database is set up and checked. This is logged in the school’s Activity, and the school’s admins and primary head are told. The school goes Live once its other required steps are done.',
  'pa.dedicatedRefLabel': 'Reference of the dedicated database',
  'pa.dedicatedRefHint': 'For example its Supabase project reference. Shown in the school’s log; never a password or key.',
  'pa.dedicatedRef': (ref: string) => `Ready · ${ref}`,
  'pa.dedicatedConfirmed': 'Dedicated database confirmed.',
  'err.not_requested': 'This school has no dedicated database waiting to be confirmed.',
  'err.reference_required': 'Enter the reference of the dedicated database.',
  'err.reference_too_long': 'Keep the reference under 200 characters.',
  'pa.changePaperwork': 'Change',
  'pa.goSettings': 'Settings',
  'pa.goClasses': 'Classes',
  'pa.goStaff': 'Staff',
  'pa.markDone': 'Mark done',
  'pa.reopen': 'Reopen',
  'pa.markDoneIntro': 'Mark this step done for the school. It’s logged with your name and the time.',
  'pa.reopenIntro': 'Reopen this step. If it’s required, check-ins stay open until you suspend the school.',
  'pa.paperworkOnlyZencera': 'Only Zencera sets the paperwork status. It’s copied to the critical-records archive.',
  'pa.nowLive': 'Saved. Every required item is done: the school is now Live.',
  'pa.about': 'About this school',
  'pa.contractRef': 'Contract reference',
  'pa.created': 'Created',
  'pa.classes': 'Classes',
  'pa.nNoTeacher': (c: number) => `${c} without a teacher`,
  'pa.primaryHead': 'Primary head of school',
  'pa.schoolAdmins': 'School admins',
  'pa.supportHistory': 'Recent support sessions',

  // lifecycle
  'pa.lifecycle': 'School lifecycle',
  'pa.lifecycleHint': 'Platform-level actions. They don’t need support mode and are recorded in the platform log and the archive.',
  'pa.suspend': 'Suspend school',
  'pa.suspendTitle': (s: string) => `Suspend ${s}?`,
  'pa.suspendIntro': 'Staff are signed out and can’t sign in. Open check-ins close; student links show that check-ins are paused, with the safety-net message. Nothing is deleted, and you can resume at any time.',
  'pa.sus_contract_lapse': 'Contract lapsed',
  'pa.sus_data_protection': 'Data-protection concern',
  'pa.sus_sandbox_ended': 'Sandbox ended, decision pending',
  'pa.sus_other': 'Other',
  'pa.suspendMessage': 'Message to staff (optional)',
  'pa.suspendMessageHint': 'Shown on the sign-in page to this school’s staff.',
  'pa.suspendedDone': 'School suspended.',
  'pa.suspendedSince': (d: string) => `Suspended since ${d}`,
  'pa.resume': 'Resume school',
  'pa.resumeTitle': (s: string) => `Resume ${s}?`,
  'pa.resumeIntro': 'Staff can sign in again and the school returns to the state it was in before.',
  'pa.resumedDone': 'School resumed.',
  'pa.convert': 'Change to Customer',
  'pa.convertTitle': 'Change Sandbox → Customer',
  'pa.convertIntro': 'The Customer checklist then applies: newly required items reopen setup and block check-ins until they’re done. This can’t be reversed.',
  'pa.convertTestsFirst': (c: number) => `Erase the school’s ${c} test ${n(c, 'account', 'accounts')} first (Staff tab).`,
  'pa.convertedDone': 'The school is now a Customer school.',
  'pa.offStepHold': (d: string) => `30-day hold ends ${d}. During the hold the school can export its data.`,
  'pa.offStepAck': 'The primary head of school acknowledges that anonymous aggregates are kept.',
  'pa.offStepAckDone': (who: string, d: string) => `Acknowledged by ${who}, ${d}.`,
  'pa.offStepConfirm': 'Type the school code to offboard.',
  'pa.recordAck': 'Record acknowledgement',
  'pa.ackIntro': 'Record the primary head of school’s written acknowledgement of this text:',
  'pa.ackText': 'Anonymous aggregates above the suppression floor are kept by Zencera after offboarding, as stated in our agreement.',
  'pa.ackBy': 'Acknowledged by',
  'pa.ackOn': 'Acknowledged on',
  'pa.offboard': 'Offboard school',
  'pa.offboardTitle': (s: string) => `Offboard ${s}?`,
  'pa.offboardIntro': 'This can’t be undone.',
  'pa.offboardErase': (c: number) => `Personal data of ${c} staff and test ${n(c, 'account', 'accounts')} is erased.`,
  'pa.offboardKeep': 'Anonymous aggregates are kept, as acknowledged.',
  'pa.offboardReport': 'The signatory’s GDPR summary report goes to the archive.',
  'pa.typeCode': (code: string) => `Type ${code} to confirm`,
  'pa.offboardedDone': 'School offboarded. The GDPR summary report is in the archive.',
  'pa.offboardedOn': (d: string) => `Offboarded ${d}. Staff data was erased; anonymous aggregates are kept.`,
  'pa.seeReport': 'See the GDPR summary report',

  // staff
  'pa.people': 'People',
  'pa.noPeople': 'No one has a role at this school yet.',
  'pa.addTestAccount': '+ Add test account',
  'pa.inviteStaff': '+ Invite staff',
  'pa.whatTestAccounts': 'add or change test accounts',
  'pa.whatInvite': 'invite or change staff',
  'pa.whatPerson': 'change this person’s roles, classes or account',
  'pa.whatClasses': 'add classes or assign teachers',
  'pa.whatSettings': 'change the school’s profile, policies or safety-net message',
  'pa.noClasses': 'No classes',
  'pa.gradeLeadOf': (g: string) => `Grade ${g} lead`,
  'pa.expires': (d: string) => `Expires ${d}`,
  'pa.lastSignIn': (d: string) => `Last sign-in: ${d}`,
  'pa.invites': 'Invites waiting',
  'pa.inviteQueued': 'Queued',
  'pa.inviteSent': 'Sent',
  'pa.cancelInvite': 'Cancel invite',
  'pa.cancelInviteFor': (who: string) => `Cancel the invite for ${who}`,
  'pa.cancelInviteIntro': 'The invite can’t be used after this. You can invite the person again later.',
  'pa.inviteCancelled': 'Invite cancelled.',
  'pa.testIntro': 'A Sandbox test account: fictional name, generated username (an address that can’t receive mail) and password, TEST badge everywhere. It’s erased automatically when it expires.',
  'pa.inviteIntro': 'The person gets an email to join (once an email provider is set up) and signs in with a one-time code.',
  'pa.fictionalName': 'Fictional display name',
  'pa.fullName': 'Full name',
  'pa.roles': 'Roles',
  'pa.role': 'Role',
  'pa.makePrimary': 'Primary head of school (if the school has none)',
  'pa.counsellorScope': 'Sees',
  'pa.wholeSchool': 'The whole school',
  'pa.chosenGrades': 'Chosen grades',
  'pa.gradeN': (g: string) => `Grade ${g}`,
  'pa.noClassesYet': 'No classes yet — add them on the Classes tab, then assign.',
  'pa.classesOptional': 'Classes (optional)',
  'pa.kindFor': (c: string) => `Assignment kind for ${c}`,
  'pa.kind': 'Kind',
  'pa.kind_homeroom': 'Homeroom teacher',
  'pa.kind_subject': 'Subject teacher',
  'pa.kind_grade_lead': 'Grade lead',
  'pa.kindHint_homeroom': 'Runs check-ins and sees the class’s results. Usually one per class.',
  'pa.kindHint_subject': 'Runs check-ins and sees the class’s results. Any number per class.',
  'pa.kindHint_grade_lead': 'Sees grade-level results; runs check-ins only in classes also assigned.',
  'pa.expiresIn': 'Expires in',
  'pa.nDays': (d: number) => `${d} days`,
  'pa.inviteLanguage': 'Invite language',
  'pa.wideningNote': 'Head of school, counsellor and school admin widen what someone can see: they wait for the primary head of school’s approval (or Zencera’s, if the school has no primary head).',
  'pa.createTest': 'Create test account',
  'pa.queueInvite': 'Queue invite',
  'pa.addAnother': 'Add another',
  'pa.testCreated': (who: string) => `Test account for ${who} created.`,
  'pa.inviteQueuedDone': (who: string) => `Invite for ${who} queued.`,
  'pa.schoolCodeForLogin': (code: string) => `On the sign-in page they first enter the school code ${code}.`,
  'pa.roleName_school_admin': 'School admin',
  'pa.roleName_head_of_school': 'Head of school',
  'pa.roleName_counsellor': 'Counsellor',
  'pa.roleName_teacher': 'Teacher',
  'pa.rolePrimaryHead': 'Head of school (primary)',
  'pa.roleDeputyHead': 'Head of school (deputy)',
  'pa.rolePending': (r: string) => `${r} — waiting for approval`,
  'pa.statusActive': 'Active',
  'pa.statusActiveTest': 'Active (TEST)',
  'pa.statusDeactivated': 'Deactivated',
  'pa.statusExpired': 'Expired',

  // person page
  'pa.personGone': 'This person is no longer on this school’s staff (they may have been erased).',
  'pa.backToStaff': '← Back to staff',
  'pa.details': 'Details',
  'pa.expiry': 'Expires',
  'pa.lastSignInLabel': 'Last sign-in',
  'pa.authenticators': 'Authenticator apps',
  'pa.otherSchools': 'Other schools',
  'pa.otherSchoolsN': (c: number) => `Also on staff at ${c} other ${n(c, 'school', 'schools')}`,
  'pa.reactivatesOn': (d: string) => `reactivates ${d}`,
  'pa.account': 'Sign-in and account',
  'pa.signOutEverywhere': 'Sign out everywhere',
  'pa.signOutIntro': (who: string) => `${who} is signed out on every device within a minute.`,
  'pa.resetPassword': 'Reset password',
  'pa.resetPasswordIntro': 'A new password is generated and shown once. The old one stops working and the person is signed out. Nothing opens with the new one until the person chooses their own password.',
  'pa.passwordReset': 'New password generated.',
  'pa.extendExpiry': 'Extend expiry',
  'pa.extendIntro': 'Sets a new expiry date counted from today (at most 90 days).',
  'pa.resetMfa': 'Reset two-factor',
  'pa.resetMfaFor': (who: string) => `Reset two-factor for ${who}`,
  'pa.resetMfaIntro': 'Removes their authenticator apps so they can set one up again. Confirm who they are outside the app first (a call to a known number, or in person).',
  'pa.resetMfaAdminIntro': 'Removes this platform admin’s authenticator apps. Confirm who they are outside the app first.',
  'pa.confirmedHow': 'How you confirmed who they are',
  'pa.deactivate': 'Deactivate',
  'pa.deactivateTitle': (who: string) => `Deactivate ${who}?`,
  'pa.deactivateIntro': (who: string, c: number) =>
    `${who} will be signed out and can’t sign in. Their ${c} class ${n(c, 'assignment', 'assignments')} will be removed. Check-in results from their sessions stay in class and school history.`,
  'pa.deactivateOther': 'This person also works at another school, so only their roles here end; their account stays active there.',
  'pa.dr_left_school': 'Left the school',
  'pa.dr_leave': 'On leave',
  'pa.dr_role_ended': 'Role ended',
  'pa.dr_other': 'Other',
  'pa.reactivateOn': 'Reactivate automatically on',
  'pa.deactivated': 'Deactivated.',
  'pa.soleTeacher': (classes: string) => `Deactivated. These classes now have no teacher: ${classes}.`,
  'pa.reassignNow': 'Reassign now',
  'pa.reactivate': 'Reactivate',
  'pa.restoreAssignments': 'Also restore their last class assignments',
  'pa.erase': 'Erase personal data',
  'pa.eraseTitle': (who: string) => `Erase ${who}’s personal data?`,
  'pa.eraseIntro': 'Their sign-in, name and email are deleted; logs will show “Former staff member #NNNN”. Session counts and aggregates are untouched. A receipt goes to the archive. This can’t be undone.',
  'pa.eraseSecondAdmin': 'This person is a school admin or the primary head: a second platform admin must approve the erasure, on the school’s written instruction.',
  'pa.eraseAfterDeactivate': 'Real staff are deactivated first; erasure is then offered.',
  'pa.er_own_request': 'The person’s own request',
  'pa.er_left_school': 'Left the school',
  'pa.er_test_cleanup': 'Test clean-up',
  'pa.er_other': 'Other',
  'pa.instruction': 'The school’s written instruction (who, when)',
  'pa.typeEmail': (e: string) => `Type ${e} to confirm`,
  'pa.erasedDone': 'Personal data erased. The receipt is in the archive.',
  'pa.eraseRequested': 'Erasure requested. It happens once a second platform admin approves it in Approvals.',
  'pa.addRole': '+ Add role',
  'pa.before': 'Now:',
  'pa.after': 'After:',
  'pa.reachHead': 'school, grade and class results',
  'pa.reachAdmin': 'staff, classes and settings — no results',
  'pa.reachCounsellorAll': 'results for every grade, whole school',
  'pa.reachCounsellorGrades': (g: string) => `results for grades ${g}`,
  'pa.reachTeacher': (c: string) => `results for classes ${c}`,
  'pa.reachTeacherNone': 'no classes yet',
  'pa.roleAddedNow': 'Role added. It applies from their next action.',
  'pa.roleToHead': 'Request sent to the primary head of school for approval.',
  'pa.roleToZencera': 'The school has no primary head, so the request is in Zencera’s Approvals. Confirm with the school’s signatory before approving.',
  'pa.removeRoleTitle': (r: string) => `Remove ${r}?`,
  'pa.removeRoleIntro': 'They lose what this role can see straight away. Removing Teacher also ends their class assignments.',
  'pa.removeRoleFor': (r: string) => `Remove the ${r} role`,
  'pa.makePrimaryAction': 'Make primary head',
  'pa.makePrimaryIntro': 'Moves the primary mark from the current primary head. Do this only after confirming with the school’s signatory; the move goes to the archive.',
  'pa.confirmedWith': 'Confirmed with (name, how, when)',
  'pa.confirmedWithHint': (s: string) => `The school’s signatory on file: ${s}.`,
  'pa.editDetails': 'Edit details',
  'pa.emailChangeLater': 'Email changes are confirmed from the new address once email sign-in is live.',
  'pa.assignments': 'Classes and grades',
  'pa.assign': 'Assign',
  'pa.assignTitle': (who: string) => `Assign ${who}`,
  'pa.grade': 'Grade',
  'pa.noClassesWho': 'No classes yet. Their dashboard tells them who to ask.',
  'pa.notATeacher': 'Only people with the Teacher role are assigned to classes.',
  'pa.unassign': 'Unassign',
  'pa.unassignFrom': (c: string) => `Unassign from ${c}?`,
  'pa.unassignIntro': 'Their access to this class’s results ends at once. An open check-in runs to its end and its responses still count.',
  'pa.unassignedOpen': 'Unassigned. A check-in is open in that class; it runs to its natural end.',
  'pa.secondHomeroom': (c: string) => `Assigned. Note: ${c} now has two homeroom teachers.`,
  'pa.activity': 'Activity',

  // classes tab
  'pa.namingConvention': 'Naming convention',
  'pa.editLists': 'Edit grade levels and sections',
  'pa.levelsNow': (l: string) => `Grade levels: ${l}`,
  'pa.sectionsNow': (s: string) => `Sections: ${s}`,
  'pa.listsIntro': 'Class labels are made from these lists (grade 7 + section A = 7A), never typed.',
  'pa.levelsInUseHint': 'A grade level with classes can’t be removed.',
  'pa.sectionScheme': 'Section scheme',
  'pa.scheme_letters': 'Letters A–F',
  'pa.scheme_numbers': 'Numbers 1–6',
  'pa.scheme_custom': 'Own list',
  'pa.sections': 'Sections',
  'pa.removeSection': (s: string) => `Remove section ${s}`,
  'pa.inUse': '(in use — it will be disabled, not deleted)',
  'pa.addSection': 'Add a section',
  'pa.sectionsInUseHint': 'A section in use is disabled, not deleted.',
  'pa.addClasses': 'Add classes',
  'pa.addClassesHint': 'Tick the classes to create. Classes that already exist are ticked and locked.',
  'pa.needLists': 'Set the grade levels and sections first.',
  'pa.gridCaption': 'Grade levels by section; tick a cell to create that class',
  'pa.classExistsLabel': (c: string) => `${c} already exists`,
  'pa.createClassLabel': (c: string) => `Create ${c}`,
  'pa.expectedSize': 'Expected size',
  'pa.expectedSizeHint': 'Required. Number of students — never names. Participation rates use it.',
  'pa.smallClassWarn': 'Under 5: this class’s results will usually be hidden and roll up to the school total.',
  'pa.createNClasses': (c: number) => `Create ${c} ${n(c, 'class', 'classes')}`,
  'pa.classesCreated': (c: number) => `${c} ${n(c, 'class', 'classes')} created.`,
  'pa.classesThisYear': (y: string) => `Classes ${y}`,
  'pa.noClassesSchool': 'No classes yet.',
  'pa.noGrade': 'Not in a grade (older classes)',
  'pa.noClassesInGrade': 'No classes in this grade yet.',
  'pa.leadChip': (who: string) => `Lead: ${who}`,
  'pa.addGradeLead': '+ Grade lead',
  'pa.addGradeLeadTo': (g: string) => `Add a grade lead to ${g}`,
  'pa.removeAssignment': (who: string, c: string) => `Remove ${who} from ${c}`,
  'pa.sizeN': (c: number) => `${c} students`,
  'pa.sizeUnknown': 'Size missing',
  'pa.noTeacher': 'No teacher',
  'pa.assignShort': 'Assign',
  'pa.assignTo': (c: string) => `Assign a teacher to ${c}`,
  'pa.size': 'Size',
  'pa.editSizeOf': (c: string) => `Expected size of ${c}`,
  'pa.archive': 'Archive',
  'pa.archiveOf': (c: string) => `Archive or delete ${c}`,
  'pa.archiveTitle': (c: string) => `Archive ${c}?`,
  'pa.archiveClassIntro': 'It leaves teachers’ pickers and its assignments end; its history is kept. You can restore it.',
  'pa.delete': 'Delete',
  'pa.deleteTitle': (c: string) => `Delete ${c}?`,
  'pa.deleteIntro': 'This class never had a check-in, so it can be deleted completely.',
  'pa.archivedDone': 'Class archived.',
  'pa.deletedDone': 'Class deleted.',
  'pa.archivedN': (c: number) => `Archived classes (${c})`,
  'pa.restore': 'Restore',
  'pa.restoreOf': (c: string) => `Restore ${c}`,
  'pa.restored': 'Class restored.',
  'pa.noTeachersYet': 'No one has the Teacher role yet. Add a person with that role first.',
  'pa.searchStaff': 'Search staff',
  'pa.teacher': 'Teacher',
  'pa.secondHomeroomWarn': 'This class already has a homeroom teacher. A second one is allowed (job-sharing).',

  // settings tab
  'pa.profile': 'School profile',
  'pa.codeNeverChanges': 'never changes',
  'pa.levelsOnClasses': 'Grade levels and sections are on the Classes tab.',
  'pa.safetyNetHint': 'Shown to students on every check-in screen, in their language. Say who to talk to and where, plus a helpline.',
  'pa.messageIn': (l: string) => `Message in ${l}`,
  'pa.useTemplate': 'Use the template',
  'pa.safetyTemplate_fi': 'Jos sinulla on huolia, voit puhua kuraattorin kanssa: [nimi], huone [numero]. Voit soittaa myös Sekasin-chattiin tai numeroon 116 111 (Lasten ja nuorten puhelin).',
  'pa.safetyTemplate_sv': 'Om något oroar dig kan du prata med kuratorn: [namn], rum [nummer]. Du kan också ringa 116 111 (Barnens och ungas telefon).',
  'pa.safetyTemplate_en': 'If something is worrying you, you can talk to the school counsellor: [name], room [number]. You can also call 116 111 (helpline for children and young people).',
  'pa.previewIn': 'Preview in',
  'pa.previewLabel': 'Preview of the student screen',
  'pa.previewEmpty': 'No message yet.',
  'pa.policies': 'Policies',
  'pa.polSelfService': 'Teachers may add themselves to classes',
  'pa.polSelfServiceDesc': 'From the school’s existing classes; the school admin is told and can undo it. Default: off.',
  'pa.polWholeSchool': 'Counsellors see the whole school',
  'pa.polWholeSchoolDesc': 'Off: counsellors see only the grades they’re assigned. Default: on.',
  'pa.confirmations': 'Setup confirmations',
  'pa.confirmationsHint': 'Steps that happen outside Swipe. Each is logged with who and when.',

  // approvals
  'pa.approvalsIntro': 'Requests that only Zencera decides: roles that widen access in a school with neither a primary head nor an approval delegate; and — decided by a second platform admin — erasures of a primary head or school admin, and appointments of a primary head on the school’s instruction.',
  'pa.waiting': 'Waiting',
  'pa.noApprovals': 'No requests are waiting.',
  'pa.apWiden': (who: string, r: string) => `${who} — add ${r}`,
  'pa.apErase': (who: string) => `${who} — erase personal data`,
  'pa.apWidenWhy': 'This school has no active primary head. Confirm with its signatory outside the app before approving.',
  'pa.apInstruction': (s: string) => `School’s instruction: ${s}`,
  'pa.apWaitingSecond': 'You requested this. It waits for a second platform admin.',
  'pa.requestedBy': (who: string, d: string) => `requested by ${who}, ${d}`,
  'pa.expiresOn': (d: string) => `expires ${d}`,
  'pa.approve': 'Approve',
  'pa.approveErase': 'Approve and erase',
  'pa.reject': 'Reject',
  'pa.approveTitle': 'Approve this request?',
  'pa.rejectTitle': 'Reject this request?',
  'pa.approved': 'Approved.',
  'pa.rejected': 'Rejected.',

  // log & archive
  'pa.logIntro': 'Append-only and tamper-evident: each row carries a hash of the one before. Shows who did what and when — never what a person saw in results.',
  'pa.logScope': 'Which log',
  'pa.logPlatform': 'Platform events',
  'pa.logAll': 'All schools and platform',
  'pa.logSchool': (s: string) => `Activity — ${s}`,
  'pa.filterAction': 'Filter by action',
  'pa.allActions': 'All actions',
  'pa.from': 'From',
  'pa.to': 'To',
  'pa.exportCsv': 'Export CSV',
  'pa.verifyChain': 'Check the hash chain',
  'pa.chainOk': (c: number) => `Chain intact (${c} rows checked)`,
  'pa.chainBroken': (id: number) => `Chain broken at row ${id}`,
  'pa.logEmpty': 'Nothing logged for these filters.',
  'pa.logMoreLoaded': (c: number) => `${c} more rows loaded.`,
  'pa.colTime': 'Time',
  'pa.colWho': 'Who',
  'pa.colRole': 'Acting as',
  'pa.colAction': 'Action',
  'pa.colTarget': 'Target',
  'pa.colDetails': 'Details',
  'pa.archiveIntro': 'Full copies of business- and regulation-critical records. Never trimmed, never edited.',
  'pa.records': 'Records',
  'pa.filterKind': 'Filter by kind',
  'pa.filterSchool': 'Filter by school',
  'pa.allKinds': 'All kinds',
  'pa.noRecords': 'No records for these filters.',
  'pa.ak_school_created': 'School created',
  'pa.ak_school_type_changed': 'School type changed',
  'pa.ak_school_state_changed': 'School state changed',
  'pa.ak_paperwork_changed': 'Paperwork changed',
  'pa.ak_access_widened': 'Access widened',
  'pa.ak_primary_head_changed': 'Primary head changed',
  'pa.ak_erasure_receipt': 'Erasure receipt',
  'pa.ak_suspension_notice': 'Suspension notice',
  'pa.ak_offboarding_acknowledged': 'Offboarding acknowledged',
  'pa.ak_offboarding_report': 'GDPR summary report',

  // platform settings
  'pa.notifEmail': 'Platform notification email',
  'pa.notifEmailOn': 'Events that need Zencera are emailed here as well as shown in the notification centre.',
  'pa.notifEmailOff': 'No email provider is set up yet, so events go to the notification centre only. Saved now, this address is used once email is live.',
  'pa.mfaEnforce': 'Two-factor for platform admins',
  'pa.mfaOnBody': 'Every platform-admin action needs a sign-in completed with an authenticator code.',
  'pa.mfaOffBody': 'Not enforced yet. Before turning it on:',
  'pa.mfaReqTwo': (c: number) => `At least two platform admins (now ${c})`,
  'pa.mfaReqAll': 'Every platform admin has an authenticator app',
  'pa.mfaReqSession': 'This sign-in used your authenticator code.',
  'pa.mfaVerifyNow': 'Verify now',
  'pa.mfaTurnOn': 'Turn on enforcement',
  'pa.mfaTurnOff': 'Turn off enforcement',
  'pa.platformAdmins': 'Platform admins',
  'pa.platformAdminsHint': 'Zencera staff with the platform-admin role. New platform admins are added out of band (README §6). Each should have two authenticators: a main and a backup.',
  'pa.health': 'Database space',
  'pa.dbSize': 'Database size',
  'pa.freeSpace': 'Free space',
  'pa.actionNeeded': 'Action needed',
  'pa.logSize': 'Audit log size',
  'pa.archiveRows': 'Archive records',
  'pa.healthHint': 'Sizes only — no school or user data. The weekly health check alerts below 0.2 GB free.',
  'pa.maintenance': 'Maintenance',
  'pa.sweepHint': 'Expired test accounts are erased automatically every night. You can run it now.',
  'pa.sweepNow': 'Erase expired test accounts now',
  'pa.sweepIntro': 'Erases every test account whose expiry has passed, with a receipt for each. This can’t be undone.',
  'pa.sweepGo': 'Erase them',
  'pa.sweepDone': (c: number) => `${c} expired test ${n(c, 'account', 'accounts')} erased.`,
  'pa.rosterHint': 'Add people from a school’s Staff tab: test accounts in Sandbox schools, invites in Customer schools.',

  // grade levels (national)
  'lvl.vl1': '1', 'lvl.vl2': '2', 'lvl.vl3': '3', 'lvl.vl4': '4', 'lvl.vl5': '5', 'lvl.vl6': '6',
  'lvl.vl7': '7', 'lvl.vl8': '8', 'lvl.vl9': '9',
  'lvl.lukio1': 'Upper sec. 1', 'lvl.lukio2': 'Upper sec. 2', 'lvl.lukio3': 'Upper sec. 3',
  'lvl.amm': 'Vocational',

  // notification centre
  'notif.title': 'Notifications',
  'notif.bell': 'Notifications',
  'notif.bellUnread': (c: number) => `Notifications, ${c} unread`,
  'notif.home': 'Home',
  'notif.unreadN': (c: number) => `${c} unread`,
  'notif.allCaughtUp': 'All caught up',
  'notif.markAll': 'Mark all as read',
  'notif.allRead': 'All marked as read.',
  'notif.empty': 'Nothing here yet. Changes that affect you show up here.',
  'notif.new': 'New',
  'notif.roleAdded': (r: string) => `You’ve been given the ${r} role.`,
  'notif.roleRemoved': (r: string) => `Your ${r} role was removed.`,
  'notif.approvalWaiting': (r: string) => `A request to add ${r} is waiting for your approval.`,
  'notif.approvalNoHead': (r: string) => `A school with no primary head needs Zencera to decide a request to add ${r}.`,
  'notif.approvalApproved': 'Your request was approved.',
  'notif.approvalRejected': 'Your request was rejected.',
  'notif.eraseSecond': 'An erasure needs a second platform admin’s approval.',
  'notif.assigned': (c: number) => `You’ve been added to ${c} ${n(c, 'class', 'classes')}.`,
  'notif.unassigned': 'You were removed from a class.',
  'notif.primaryHead': 'You’re now the primary head of school.',
  'notif.paperwork': (s: string) => `Paperwork status changed: ${s}.`,
  'notif.schoolLive': 'Setup is complete: check-ins can open.',
  'notif.dedicatedRequested': 'Tier B: set up a dedicated database for this school, then confirm it on the school page.',
  'notif.dedicatedCancelled': 'Tier B cancelled: this school no longer needs a dedicated database.',
  'notif.dedicatedPending': 'Zencera is setting up your school’s dedicated database. You’ll be told when it’s ready.',
  'notif.dedicatedReady': 'Your school’s dedicated database is ready.',
  'notif.reactivated': 'A staff member was reactivated automatically.',

  // head of school + teacher
  'pa.soTitle': 'Inside the school — support mode only',
  'pa.soBody': (what: string) => `${what} belong to the school. Zencera sees and changes them only in support mode: with a reason, for two hours, logged in the school’s own Activity and the platform log.`,
  'pa.soHint': 'The school’s summary, setup checklist, lifecycle and Activity stay available without it.',
  'pa.soStaff': 'Staff names, emails, roles and sign-ins',
  'pa.soClasses': 'Classes and teacher assignments',
  'pa.soSettings': 'The school’s profile, policies and safety-net message',
  'pa.soPerson': 'This person’s details, roles and account',
  'pa.soOffboardedTitle': 'This school has left Swipe',
  'pa.soOffboardedBody': 'Its staff data was erased at offboarding. The GDPR summary report is in the archive.',
  'pa.apNeedsSupport': 'Deciding enters support mode for this school first (logged in its Activity).',
  'head.approvals': (c: number) => `Approvals waiting (${c})`,
  'head.approvalsHint': 'Roles that widen what someone can see wait for you as primary head of school. Requests expire after 14 days.',
  'teacher.notReady': 'Your school hasn’t finished setup, so check-ins can’t open yet. Ask your school admin.',
  'teacher.startFailed': 'The check-in couldn’t be opened. Try again.',

  // ── first sign-in of a test account (0011) ──
  'err.password_not_changed': 'Your new password wasn’t saved yet. Choose a new password and try again.',
  'err.password_change_required': 'Choose your own password first.',
  'pa.pendingRolesNote': (roles: string) =>
    `Waiting for the primary head of school to approve: ${roles}. The account can use these roles once approved.`,
  'pw.title': 'Choose your own password',
  'pw.intro': (name: string) =>
    `Welcome, ${name}. The password you were given only lets you sign in once. Choose a new one that only you know — nothing opens until you do.`,
  'pw.new': 'New password',
  'pw.again': 'New password again',
  'pw.hint': (min: number) => `At least ${min} characters. A few unrelated words make a good password.`,
  'pw.tooShort': (min: number) => `Use at least ${min} characters.`,
  'pw.mismatch': 'The two passwords are different. Type the same password in both fields.',
  'pw.sameAsBefore': 'That’s the password you were given. Choose a different one.',
  'pw.weak': 'That password is too easy to guess. Choose a longer or less common one.',
  'pw.save': 'Save password',
  'pw.saving': 'Saving…',
  'pw.retry': 'Try again',
  'pw.failed': 'The password couldn’t be saved. Check your connection and try again.',
  'pw.savedRetry': 'Your new password is saved, but Swipe couldn’t confirm it yet. Try again — you don’t need to type it again.',
};

export type PlatformStrings = Shape<typeof platformEn>;
